import React, { Suspense, lazy } from 'react';
import { string } from 'prop-types';
import {
  deviceDetect,
  isMobileOnly,
  useDeviceData,
  useDeviceSelectors,
  useMobileOrientation,
} from 'react-device-detect';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './App.css';
import { redirectToLogin } from './common/CommonTools';
import BblButton from './component/buttons/bblButton';
import {
  BgmKey,
  SoundEffects,
  useSoundEffects,
} from './component/soundEffect/soundEffect';
import { useAppSelector } from './store/hooks';
// Redux
import { selectAppPageStep } from './store/reducer/appStateSlice';
import { outsideComponentDispatch } from './store/store';
import { SystemUpgradeResetPw } from './view/SystemUpgradeResetPw';
import { BblLoading } from './view/bigbanglab/BblLoading';
import { ChildProfile } from './view/childProfile';
import { ClientOffline } from './view/clientOffline/clientOffline';
// Pages
import { Entry } from './view/entry';
import { ForgetEmail } from './view/forgetEmail';
import { ForgetPassword } from './view/forgetPassword';
import { GenericNotificationPage } from './view/genericNotificationPage/genericNotificationPage';
import { Login } from './view/login';
import { NotFound } from './view/notFound';
import { PaymentProcessing } from './view/paymentProcessing/PaymentProcessing';
import { ResetPassword } from './view/resetPassword';
import { SignUp } from './view/signup';
import { Subscription } from './view/subscription';
import { SystemMaintenance } from './view/systemMaintenance/systemMaintenance';
import { Thankyou } from './view/thankYou';
import { UseLargerScreen } from './view/useLargerScreen/userLargerScreen';

//
export const SoundEffectsContext = React.createContext<SoundEffects>({
  play(key?: BgmKey) {},
  stop() {},
  muted: false,
  setMuted() {},
});

//
function App(): JSX.Element {
  // LOCAL Hooks

  const soundEffects = useSoundEffects();

  // PAGE FUNCTIONS
  const { isLandscape } = useMobileOrientation();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const device = useDeviceSelectors(window.navigator.userAgent);

  // React.useEffect(() => {
  //   console.log('isMobileOnly', isMobileOnly);
  //   console.log('isLandscape', isLandscape);
  //   console.log('device', device);
  // }, [isMobileOnly, isLandscape, device]);

  // GLOBAL LISTENERS
  window.addEventListener('popstate', (event) => {
    const window = event.target as Window;
    window.history.go(1);
  });

  // a function to retry loading a chunk to avoid chunk load error for out of date code
  const lazyRetry = async (
    componentImport: () => Promise<{
      default: React.ComponentType<any>;
    }>,
    name: 'bbl' | 'pp',
  ): Promise<{
    default: React.ComponentType<any>;
  }> => {
    const key = `${name}-lazy-refreshed`;
    return new Promise<{
      default: React.ComponentType<any>;
    }>((resolve, reject) => {
      // check if the window has already been refreshed
      const hasRefreshed = JSON.parse(
        window.sessionStorage.getItem(key) ?? '0',
      ) as number;
      // try to import the component
      componentImport()
        .then((component) => {
          window.sessionStorage.setItem(key, '0'); // success so reset the refresh
          resolve(component);
        })
        .catch((error) => {
          console.error(error);
          if (hasRefreshed < 2) {
            // not been refreshed yet
            window.sessionStorage.setItem(key, String(hasRefreshed + 1)); // we are now going to refresh
            window.location.reload();
            return; // refresh the page
          }

          // Default error behaviour as already tried refresh
          resolve({
            default: () => {
              window.sessionStorage.setItem(key, '0'); // success so reset the refresh
              return (
                <GenericNotificationPage
                  iconUrl={'assets/bigBangLab/undermaintenance.png'}
                  title={`${
                    name === 'bbl' ? 'Big Bang Lab' : 'Parent Portal'
                  } Loading Error`}
                  subtitle={`${
                    name === 'bbl' ? 'Big Bang Lab' : 'Parent Portal'
                  } cannot be accessed now.
          Please try again later.`}
                  errMsg={error}
                  button={
                    <BblButton
                      mute
                      color="primary"
                      size="medium"
                      onClick={redirectToLogin}
                    >
                      Back To Home
                    </BblButton>
                  }
                />
              );
            },
          });
        });
    });
  };

  const BigBangLab = lazy(async () => {
    return lazyRetry(async () => {
      return import('./view/bigbanglab');
    }, 'bbl');
  });

  const ParentPortal = lazy(async () => {
    return lazyRetry(async () => {
      return import('./view/parentPortal');
    }, 'pp');
  });

  return (
    <SoundEffectsContext.Provider value={soundEffects}>
      <BrowserRouter>
        <Routes>
          {/* General */}
          <Route path="/" element={<Navigate to={'/Login'} />} />
          <Route path="*" element={<Navigate to={'/Login'} />} />

          <Route path="/Signup" element={<SignUp />} />
          <Route path="/Subscription" element={<Subscription />} />
          <Route path="/Success" element={<Thankyou />} />
          <Route path="/PaymentProcessing" element={<PaymentProcessing />} />
          <Route path="/Login" element={<Login />} />
          <Route path="/ChildProfile" element={<ChildProfile />} />
          <Route path="/ForgetEmail" element={<ForgetEmail />} />
          <Route path="/ResetPassword" element={<ResetPassword />} />
          <Route path="/ForgetPassword" element={<ForgetPassword />} />

          <Route path="/NotFound" element={<NotFound />} />
          <Route path="/404" element={<NotFound />} />
          <Route path="/Maintenance" element={<SystemMaintenance />} />
          <Route path="/IncompatibleDevice" element={<UseLargerScreen />} />
          <Route path="/userOffline" element={<ClientOffline />} />

          {/* BBL */}
          {/* Parent side */}
          <Route
            path="/ParentPortal"
            element={
              <Suspense fallback={<BblLoading />}>
                <ParentPortal />
              </Suspense>
            }
          />

          {/* Student side */}
          <Route
            path="/BigBangLab"
            element={
              <Suspense fallback={<BblLoading />}>
                <BigBangLab />
              </Suspense>
            }
          />

          {/* deprecated */}
          <Route path="/Entry" element={<Entry />} />
          <Route
            path="/SystemUpgradePasswordUpgrade"
            element={<SystemUpgradeResetPw />}
          />
        </Routes>
      </BrowserRouter>
    </SoundEffectsContext.Provider>
  );
}

export default App;
